import {
  Component,
  afterNextRender,
  signal,
  HostListener,
  inject,
  OnDestroy,
} from '@angular/core';
import { Router, RouterModule } from '@angular/router';
import { App as CapacitorApp } from '@capacitor/app';
import { ToastService } from '@millionshow/client-libs';
import { PluginListenerHandle } from '@capacitor/core';

@Component({
  imports: [RouterModule],
  selector: 'app-root',
  templateUrl: './app.html',
  styleUrl: './app.css',
})
export class App implements OnDestroy {
  protected title = 'kiosk';

  // 디자인 기준 해상도 (1080 x 1920 = 9:16)
  // 만약 정확히 9:19 비율을 원하신다면 1080 x 2280으로 변경 가능합니다.
  protected readonly TARGET_WIDTH = 1080;
  protected readonly TARGET_HEIGHT = 1920;

  protected scale = signal(1);

  private readonly router = inject(Router);
  private readonly toastService = inject(ToastService);

  // 앱 종료 확인을 위한 상태
  private backButtonPressedOnce = false;
  private backButtonTimeout: ReturnType<typeof setTimeout> | null = null;
  private backButtonListener: PluginListenerHandle | null = null;

  constructor() {
    afterNextRender(() => {
      this.updateScale();
      void this.initBackButtonHandler();
    });
  }

  ngOnDestroy() {
    // 리스너 정리
    if (this.backButtonListener) {
      this.backButtonListener.remove();
    }
    if (this.backButtonTimeout) {
      clearTimeout(this.backButtonTimeout);
    }
  }

  @HostListener('window:resize')
  onResize() {
    this.updateScale();
  }

  private updateScale() {
    // 세로 길이를 화면 높이에 100% 맞춤
    const scale = window.innerHeight / this.TARGET_HEIGHT;
    this.scale.set(scale);
  }

  /**
   * Capacitor 앱의 하드웨어 뒤로가기 버튼 핸들러 초기화
   */
  private async initBackButtonHandler() {
    this.backButtonListener = await CapacitorApp.addListener(
      'backButton',
      () => {
        this.handleBackButton();
      }
    );
  }

  /**
   * 뒤로가기 버튼 처리 로직
   * - 루트 경로('/')가 아닌 경우: 이전 페이지로 이동
   * - 루트 경로('/')인 경우: 2초 내에 다시 누르면 앱 종료
   */
  private handleBackButton() {
    const currentUrl = this.router.url;

    // 루트 경로가 아닌 경우 이전 페이지로 이동
    if (currentUrl !== '/') {
      history.back();
      return;
    }

    // 루트 경로인 경우 앱 종료 로직
    if (this.backButtonPressedOnce) {
      // 2초 내에 다시 눌렀으면 앱 종료
      CapacitorApp.exitApp();
    } else {
      // 첫 번째 뒤로가기: 토스트 메시지 표시
      this.backButtonPressedOnce = true;
      this.toastService.info('한 번 더 누르면 앱이 종료됩니다.');

      // 2초 후 상태 초기화
      this.backButtonTimeout = setTimeout(() => {
        this.backButtonPressedOnce = false;
      }, 2000);
    }
  }
}
